import type {
  AnnotationLayerSpec,
  AnnotationMark,
  Box,
  HitRegion,
  Point,
  ResolvedFigure,
  SceneAnchor,
  SceneShape,
  SemanticRelation,
} from '../model.js';
import { point, roundGeometry } from '../scene-builder.js';

const MAX_MARKS = 32;
const STROKE_AMPLITUDE = 0.55;
const EDGE_INSET = 3;

export interface ResolvedAnnotationLayer {
  id: string;
  baseFigureId: string;
  viewport: ResolvedFigure['viewport'];
  shapes: SceneShape[];
  anchors: SceneAnchor[];
  hitRegions: HitRegion[];
  relations: SemanticRelation[];
  activeSemanticIds: string[];
  geometryFingerprint: string;
}

/**
 * Resolves coordinate-free teacher marks against an already-resolved figure.
 * The returned shapes form a separate overlay and never mutate or replace base
 * geometry. Every random-looking bend is derived from layer seed + stable mark
 * ID, while attachment endpoints remain exact semantic anchors.
 */
export function compileAnnotationLayer(
  layer: AnnotationLayerSpec,
  figure: ResolvedFigure,
): ResolvedAnnotationLayer {
  validateLayer(layer, figure);

  const shapes: SceneShape[] = [];
  const anchors: SceneAnchor[] = [];
  const relations: SemanticRelation[] = [];
  const activeSemanticIds: string[] = [];

  for (const mark of layer.marks) {
    const semanticId = annotationSemanticId(layer.id, mark.id);
    activeSemanticIds.push(semanticId);

    if (mark.kind === 'arrow') {
      const from = resolveAnchor(figure, mark.fromAnchorId!, mark.id);
      const to = resolveAnchor(figure, mark.toAnchorId!, mark.id);
      shapes.push(arrowShape(layer, mark, semanticId, from.point, to.point, figure.viewport.width, figure.viewport.height));
      anchors.push(
        { id: `${semanticId}.start`, semanticId, point: from.point },
        { id: `${semanticId}.end`, semanticId, point: to.point },
      );
      relations.push({
        id: `${semanticId}.connects`,
        type: 'annotation-connects',
        from: from.semanticId,
        to: to.semanticId,
        label: `Teacher arrow connects ${mark.fromAnchorId!} to ${mark.toAnchorId!}`,
      });
      continue;
    }

    const target = resolveSemanticBounds(figure, mark.targetSemanticId!, mark.id);
    if (mark.kind === 'circle') {
      const bounds = inflateAndClamp(target, 5, figure.viewport.width, figure.viewport.height);
      shapes.push(circleShape(layer, mark, semanticId, bounds));
      anchors.push({ id: `${semanticId}.center`, semanticId, point: center(bounds) });
    } else if (mark.kind === 'underline') {
      const start = point(target.x, Math.min(figure.viewport.height - EDGE_INSET, target.y + target.height + 4));
      const end = point(target.x + target.width, start.y);
      shapes.push(curvedLineShape(layer, mark, semanticId, start, end, false, figure.viewport.width, figure.viewport.height));
      anchors.push(
        { id: `${semanticId}.start`, semanticId, point: start },
        { id: `${semanticId}.end`, semanticId, point: end },
      );
    } else if (mark.kind === 'brace') {
      const placement = bracePlacement(target, figure.viewport.width);
      shapes.push(braceShape(layer, mark, semanticId, placement.start, placement.end, placement.facing));
      anchors.push(
        { id: `${semanticId}.start`, semanticId, point: placement.start },
        { id: `${semanticId}.end`, semanticId, point: placement.end },
      );
    } else {
      const note = placeNote(mark.text!, target, figure.viewport.width, figure.viewport.height);
      shapes.push(
        noteBoxShape(layer, mark, semanticId, note.bounds),
        {
          kind: 'text',
          id: `${semanticId}.text`,
          semanticId,
          role: 'annotation',
          at: point(note.bounds.x + 9, note.bounds.y + note.bounds.height / 2 + 4),
          text: compactNote(mark.text!, note.characterBudget),
          size: note.fontSize,
          weight: 700,
        },
        curvedLineShape(layer, mark, `${semanticId}.leader`, note.leaderStart, note.leaderEnd, false, figure.viewport.width, figure.viewport.height),
      );
      anchors.push(
        { id: `${semanticId}.note`, semanticId, point: center(note.bounds) },
        { id: `${semanticId}.target`, semanticId, point: note.leaderEnd },
      );
    }

    relations.push({
      id: `${semanticId}.annotates`,
      type: `annotation-${mark.kind}`,
      from: semanticId,
      to: mark.targetSemanticId!,
      label: `${mark.kind} annotates ${mark.targetSemanticId!}`,
    });
  }

  const geometryFingerprint = fnv1a(JSON.stringify({
    base: figure.geometryFingerprint,
    viewport: figure.viewport,
    shapes: shapes.map(geometryOf),
    anchors,
  }));

  return {
    id: layer.id,
    baseFigureId: figure.id,
    viewport: { ...figure.viewport },
    shapes,
    anchors,
    hitRegions: [],
    relations,
    activeSemanticIds,
    geometryFingerprint,
  };
}

export function annotationSemanticId(layerId: string, markId: string): string {
  return `annotation.${layerId}.${markId}`;
}

export function validateAnnotationLayer(layer: AnnotationLayerSpec, figure: ResolvedFigure): void {
  validateLayer(layer, figure);
}

function validateLayer(layer: AnnotationLayerSpec, figure: ResolvedFigure): void {
  if (!/^[a-z][a-z0-9-]*$/.test(layer.id)) throw new Error(`annotation-layer: invalid layer id ${layer.id}`);
  if (!Number.isSafeInteger(layer.seed) || layer.seed < 0 || layer.seed > 0xffffffff) {
    throw new Error('annotation-layer: seed must be an unsigned 32-bit integer');
  }
  if (layer.marks.length < 1 || layer.marks.length > MAX_MARKS) {
    throw new Error(`annotation-layer: require 1…${MAX_MARKS} marks`);
  }
  const ids = layer.marks.map((mark) => mark.id);
  if (new Set(ids).size !== ids.length) throw new Error('annotation-layer: mark IDs must be unique');

  for (const mark of layer.marks) {
    if (!/^[a-z][a-z0-9-]*$/.test(mark.id)) throw new Error(`annotation-layer: invalid mark id ${mark.id}`);
    if (mark.kind === 'arrow') {
      if (!mark.fromAnchorId || !mark.toAnchorId) throw new Error(`annotation-layer: arrow ${mark.id} requires fromAnchorId and toAnchorId`);
      if (mark.targetSemanticId || mark.text) throw new Error(`annotation-layer: arrow ${mark.id} accepts anchors only`);
      const from = resolveAnchor(figure, mark.fromAnchorId, mark.id);
      const to = resolveAnchor(figure, mark.toAnchorId, mark.id);
      if (from.point.x === to.point.x && from.point.y === to.point.y) {
        throw new Error(`annotation-layer: arrow ${mark.id} endpoints must be distinct`);
      }
      continue;
    }

    if (!mark.targetSemanticId) throw new Error(`annotation-layer: ${mark.kind} ${mark.id} requires targetSemanticId`);
    if (mark.fromAnchorId || mark.toAnchorId) throw new Error(`annotation-layer: ${mark.kind} ${mark.id} does not accept anchor endpoints`);
    resolveSemanticBounds(figure, mark.targetSemanticId, mark.id);
    if (mark.kind === 'note') {
      if (!mark.text?.trim()) throw new Error(`annotation-layer: note ${mark.id} requires text`);
      if (mark.text.length > 80) throw new Error(`annotation-layer: note ${mark.id} text exceeds 80 characters`);
    } else if (mark.text) {
      throw new Error(`annotation-layer: ${mark.kind} ${mark.id} does not accept text`);
    }
  }
}

function resolveAnchor(figure: ResolvedFigure, anchorId: string, markId: string): SceneAnchor {
  const matches = figure.anchors.filter((anchor) => anchor.id === anchorId);
  if (matches.length === 0) throw new Error(`annotation-layer: mark ${markId} references missing anchor ${anchorId}`);
  if (matches.length > 1) throw new Error(`annotation-layer: mark ${markId} references ambiguous anchor ${anchorId}`);
  return matches[0]!;
}

function resolveSemanticBounds(figure: ResolvedFigure, semanticId: string, markId: string): Box {
  const hits = figure.hitRegions.filter((hit) => hit.semanticId === semanticId);
  if (hits.length > 1) throw new Error(`annotation-layer: mark ${markId} references ambiguous target ${semanticId}`);
  if (hits.length === 1) return { ...hits[0]!.box };

  const boxes = figure.shapes
    .filter((shape) => shape.semanticId === semanticId)
    .map(shapeBounds)
    .filter((bounds): bounds is Box => bounds !== undefined);
  if (boxes.length === 0) throw new Error(`annotation-layer: mark ${markId} references missing target ${semanticId}`);
  return unionBoxes(boxes);
}

function shapeBounds(shape: SceneShape): Box | undefined {
  if (shape.kind === 'rect') return { ...shape.box };
  if (shape.kind === 'polygon') return boundsOfPoints(shape.points);
  if (shape.kind === 'line') return boundsOfPoints([shape.from, shape.to]);
  if (shape.kind === 'text') {
    const width = Math.max(shape.size * 0.58, shape.text.length * shape.size * (shape.mono ? 0.61 : 0.54));
    const x = shape.align === 'middle' ? shape.at.x - width / 2 : shape.align === 'end' ? shape.at.x - width : shape.at.x;
    return { x, y: shape.at.y - shape.size, width, height: shape.size * 1.25 };
  }
  const numbers = [...shape.d.matchAll(/-?\d+(?:\.\d+)?/g)].map((match) => Number(match[0]));
  const points: Point[] = [];
  for (let index = 0; index + 1 < numbers.length; index += 2) points.push(point(numbers[index]!, numbers[index + 1]!));
  return points.length ? boundsOfPoints(points) : undefined;
}

function boundsOfPoints(points: Point[]): Box {
  const xs = points.map((value) => value.x);
  const ys = points.map((value) => value.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

function unionBoxes(boxes: Box[]): Box {
  const x = Math.min(...boxes.map((value) => value.x));
  const y = Math.min(...boxes.map((value) => value.y));
  const right = Math.max(...boxes.map((value) => value.x + value.width));
  const bottom = Math.max(...boxes.map((value) => value.y + value.height));
  return { x, y, width: right - x, height: bottom - y };
}

function inflateAndClamp(bounds: Box, padding: number, width: number, height: number): Box {
  const left = clamp(bounds.x - padding, EDGE_INSET, width - EDGE_INSET);
  const top = clamp(bounds.y - padding, EDGE_INSET, height - EDGE_INSET);
  const right = clamp(bounds.x + bounds.width + padding, left + 1, width - EDGE_INSET);
  const bottom = clamp(bounds.y + bounds.height + padding, top + 1, height - EDGE_INSET);
  return { x: roundGeometry(left), y: roundGeometry(top), width: roundGeometry(right - left), height: roundGeometry(bottom - top) };
}

function circleShape(layer: AnnotationLayerSpec, mark: AnnotationMark, semanticId: string, bounds: Box): SceneShape {
  const centerPoint = center(bounds);
  const rx = bounds.width / 2;
  const ry = bounds.height / 2;
  const count = 14;
  const points: Point[] = [];
  for (let index = 0; index < count; index += 1) {
    const angle = (Math.PI * 2 * index) / count;
    const radial = jitter(`${layer.seed}:${mark.id}:circle:${index}`, STROKE_AMPLITUDE);
    points.push(point(
      centerPoint.x + Math.cos(angle) * Math.max(1, rx + radial),
      centerPoint.y + Math.sin(angle) * Math.max(1, ry + radial),
    ));
  }
  const d = `M ${n(points[0]!.x)} ${n(points[0]!.y)} ${points.slice(1).map((value) => `L ${n(value.x)} ${n(value.y)}`).join(' ')} Z`;
  return { kind: 'path', id: `${semanticId}.stroke`, semanticId, role: 'annotation', d };
}

function arrowShape(
  layer: AnnotationLayerSpec,
  mark: AnnotationMark,
  semanticId: string,
  from: Point,
  to: Point,
  viewportWidth: number,
  viewportHeight: number,
): SceneShape {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const normalLength = Math.max(1, Math.hypot(dx, dy));
  const bend = jitter(`${layer.seed}:${mark.id}:arrow`, STROKE_AMPLITUDE);
  const normalX = -dy / normalLength;
  const normalY = dx / normalLength;
  const c1 = point(
    clamp(from.x + dx * 0.34 + normalX * bend, EDGE_INSET, viewportWidth - EDGE_INSET),
    clamp(from.y + dy * 0.34 + normalY * bend, EDGE_INSET, viewportHeight - EDGE_INSET),
  );
  const c2 = point(
    clamp(from.x + dx * 0.68 + normalX * bend, EDGE_INSET, viewportWidth - EDGE_INSET),
    clamp(from.y + dy * 0.68 + normalY * bend, EDGE_INSET, viewportHeight - EDGE_INSET),
  );
  return {
    kind: 'path',
    id: `${semanticId}.stroke`,
    semanticId,
    role: 'annotation',
    d: `M ${n(from.x)} ${n(from.y)} C ${n(c1.x)} ${n(c1.y)} ${n(c2.x)} ${n(c2.y)} ${n(to.x)} ${n(to.y)}`,
    markerEnd: true,
  };
}

function curvedLineShape(
  layer: AnnotationLayerSpec,
  mark: AnnotationMark,
  semanticId: string,
  from: Point,
  to: Point,
  markerEnd: boolean,
  viewportWidth: number,
  viewportHeight: number,
): SceneShape {
  const midpoint = point(
    clamp((from.x + to.x) / 2 + jitter(`${layer.seed}:${mark.id}:${semanticId}:x`, STROKE_AMPLITUDE), EDGE_INSET, viewportWidth - EDGE_INSET),
    clamp((from.y + to.y) / 2 + jitter(`${layer.seed}:${mark.id}:${semanticId}:y`, STROKE_AMPLITUDE), EDGE_INSET, viewportHeight - EDGE_INSET),
  );
  return {
    kind: 'path',
    id: `${semanticId}.stroke`,
    semanticId,
    role: 'annotation',
    d: `M ${n(from.x)} ${n(from.y)} Q ${n(midpoint.x)} ${n(midpoint.y)} ${n(to.x)} ${n(to.y)}`,
    ...(markerEnd ? { markerEnd: true } : {}),
  };
}

function bracePlacement(bounds: Box, viewportWidth: number): { start: Point; end: Point; facing: 1 | -1 } {
  const right = bounds.x + bounds.width + 7;
  const useRight = right + 8 <= viewportWidth - EDGE_INSET;
  const x = useRight ? right : Math.max(EDGE_INSET + 7, bounds.x - 7);
  return {
    start: point(x, bounds.y),
    end: point(x, bounds.y + bounds.height),
    facing: useRight ? -1 : 1,
  };
}

function braceShape(
  layer: AnnotationLayerSpec,
  mark: AnnotationMark,
  semanticId: string,
  start: Point,
  end: Point,
  facing: 1 | -1,
): SceneShape {
  const middleY = (start.y + end.y) / 2;
  const hook = 6 * facing;
  const wobble = jitter(`${layer.seed}:${mark.id}:brace`, STROKE_AMPLITUDE);
  const d = [
    `M ${n(start.x)} ${n(start.y)}`,
    `Q ${n(start.x + hook + wobble)} ${n(start.y)} ${n(start.x + hook)} ${n(start.y + (middleY - start.y) * 0.68)}`,
    `Q ${n(start.x + hook)} ${n(middleY)} ${n(start.x + hook * 1.7)} ${n(middleY)}`,
    `Q ${n(start.x + hook)} ${n(middleY)} ${n(start.x + hook)} ${n(middleY + (end.y - middleY) * 0.32)}`,
    `Q ${n(start.x + hook + wobble)} ${n(end.y)} ${n(end.x)} ${n(end.y)}`,
  ].join(' ');
  return { kind: 'path', id: `${semanticId}.stroke`, semanticId, role: 'annotation', d };
}

interface NotePlacement {
  bounds: Box;
  leaderStart: Point;
  leaderEnd: Point;
  characterBudget: number;
  fontSize: number;
}

function placeNote(text: string, target: Box, viewportWidth: number, viewportHeight: number): NotePlacement {
  const margin = EDGE_INSET + 2;
  const fontSize = viewportWidth <= 340 ? 8.5 : 9.5;
  const desiredWidth = Math.min(174, Math.max(92, text.length * fontSize * 0.55 + 18));
  const width = Math.min(desiredWidth, viewportWidth - margin * 2);
  const height = 31;
  const gap = 9;
  const targetCenter = center(target);
  const candidates = [
    { x: targetCenter.x - width / 2, y: target.y - gap - height, edge: 'bottom' as const },
    { x: targetCenter.x - width / 2, y: target.y + target.height + gap, edge: 'top' as const },
    { x: target.x + target.width + gap, y: targetCenter.y - height / 2, edge: 'left' as const },
    { x: target.x - gap - width, y: targetCenter.y - height / 2, edge: 'right' as const },
  ];
  const fitting = candidates.find((candidate) => (
    candidate.x >= margin
    && candidate.y >= margin
    && candidate.x + width <= viewportWidth - margin
    && candidate.y + height <= viewportHeight - margin
  ));
  const selected = fitting ?? candidates[0]!;
  const bounds: Box = {
    x: roundGeometry(clamp(selected.x, margin, viewportWidth - margin - width)),
    y: roundGeometry(clamp(selected.y, margin, viewportHeight - margin - height)),
    width: roundGeometry(width),
    height,
  };
  const edge = fitting?.edge ?? selected.edge;
  const leaderStart = edge === 'bottom'
    ? point(bounds.x + bounds.width / 2, bounds.y + bounds.height)
    : edge === 'top'
      ? point(bounds.x + bounds.width / 2, bounds.y)
      : edge === 'left'
        ? point(bounds.x, bounds.y + bounds.height / 2)
        : point(bounds.x + bounds.width, bounds.y + bounds.height / 2);
  return {
    bounds,
    leaderStart,
    leaderEnd: targetCenter,
    characterBudget: Math.max(8, Math.floor((bounds.width - 18) / (fontSize * 0.55))),
    fontSize,
  };
}

function noteBoxShape(layer: AnnotationLayerSpec, mark: AnnotationMark, semanticId: string, bounds: Box): SceneShape {
  const corners = [
    point(bounds.x, bounds.y),
    point(bounds.x + bounds.width, bounds.y),
    point(bounds.x + bounds.width, bounds.y + bounds.height),
    point(bounds.x, bounds.y + bounds.height),
  ].map((value, index) => point(
    value.x + jitter(`${layer.seed}:${mark.id}:note:x:${index}`, STROKE_AMPLITUDE),
    value.y + jitter(`${layer.seed}:${mark.id}:note:y:${index}`, STROKE_AMPLITUDE),
  ));
  const d = `M ${n(corners[0]!.x)} ${n(corners[0]!.y)} ${corners.slice(1).map((value) => `L ${n(value.x)} ${n(value.y)}`).join(' ')} Z`;
  return { kind: 'path', id: `${semanticId}.box`, semanticId, role: 'annotation', d };
}

function compactNote(value: string, budget: number): string {
  if (value.length <= budget) return value;
  return `${value.slice(0, Math.max(1, budget - 1)).trimEnd()}…`;
}

function center(bounds: Box): Point {
  return point(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2);
}

function geometryOf(shape: SceneShape): unknown {
  if (shape.kind === 'path') return { kind: shape.kind, id: shape.id, d: shape.d, markerEnd: shape.markerEnd };
  if (shape.kind === 'text') return { kind: shape.kind, id: shape.id, at: shape.at, text: shape.text, size: shape.size };
  if (shape.kind === 'rect') return { kind: shape.kind, id: shape.id, box: shape.box };
  if (shape.kind === 'line') return { kind: shape.kind, id: shape.id, from: shape.from, to: shape.to };
  return { kind: shape.kind, id: shape.id, points: shape.points };
}

function jitter(seed: string, amplitude: number): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (((hash >>> 0) / 4294967295) * 2 - 1) * amplitude;
}

function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function n(value: number): string {
  return String(roundGeometry(value));
}
